import { Button, Popover, PopoverContent, PopoverTrigger } from '@quadralize/ui'
import { cn } from '@quadralize/utils'
import {
  AltArrowDown,
  Bolt,
  Calendar,
  Logout,
  Magnifier,
  MapPoint,
  UserCircle,
} from '@solar-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useUserLocation } from '@/hooks/use-user-location'
import { useAppTopbar } from './use-app-topbar'

export function AppTopbar() {
  const { t } = useTranslation('play')
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const { requestLocation } = useUserLocation()
  const {
    query,
    setQuery,
    selectedCity,
    loggedIn,
    userName,
    userInitials,
    handleSubmit,
    handleNavigate,
    handleLogout,
  } = useAppTopbar()

  function handleMenuNavigate(path: string): void {
    setIsUserMenuOpen(false)
    handleNavigate(path)
  }

  function handleMenuLogout(): void {
    setIsUserMenuOpen(false)
    handleLogout()
  }

  return (
    <header className="hidden h-17 shrink-0 items-center gap-4 border-b border-border-default bg-bg-base px-7 md:flex">
      <form onSubmit={handleSubmit} className="w-full max-w-lg flex-1">
        <div className="flex h-10 items-center gap-2.5 rounded-xl border border-transparent bg-bg-muted px-3.5 transition-colors focus-within:border-primary focus-within:bg-bg-base focus-within:ring-3 focus-within:ring-brand-primary-100 dark:focus-within:ring-brand-primary-900">
          <Magnifier className="h-4.5 w-4.5 shrink-0 text-text-tertiary" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('topbar.searchPlaceholder')}
            className="min-w-0 flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          <button
            type="button"
            onClick={() => requestLocation()}
            aria-label={t('topbar.updateLocation')}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-bg-base px-2.5 py-1 text-xs font-semibold text-text-secondary shadow-sm transition-colors hover:text-text-primary"
          >
            <MapPoint className="h-3 w-3 text-primary" weight="Bold" />
            {selectedCity}
          </button>
        </div>
      </form>

      <div className="ml-auto flex items-center gap-2.5">
        <Button
          type="button"
          variant="neutral"
          size="sm"
          onClick={() => handleNavigate('/buscar')}
          startIcon={<Bolt className="h-3.5 w-3.5 text-primary" weight="Bold" />}
        >
          {t('topbar.playNow')}
        </Button>

        {loggedIn ? (
          <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="group flex cursor-pointer items-center gap-2 outline-none"
                aria-label={t('topbar.accountMenu')}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary font-display text-xs font-bold text-primary-fg">
                  {userInitials}
                </span>
                <AltArrowDown
                  className={cn(
                    'h-3.5 w-3.5 text-text-tertiary transition-transform',
                    isUserMenuOpen && 'rotate-180',
                  )}
                />
              </button>
            </PopoverTrigger>

            <PopoverContent align="end" className="w-56 p-0" sideOffset={8}>
              <div className="flex items-center gap-3 border-b border-border-subtle p-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary font-display text-xs font-bold text-primary-fg">
                  {userInitials}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-text-primary">{userName}</p>
                  <p className="truncate text-xs text-text-tertiary">{t('nav.myAccount')}</p>
                </div>
              </div>

              <div className="p-1">
                <button
                  type="button"
                  onClick={() => handleMenuNavigate('/conta')}
                  className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-subtle hover:text-text-primary"
                >
                  <UserCircle className="h-4 w-4 shrink-0 text-text-secondary" weight="Bold" />
                  {t('nav.myAccount')}
                </button>
                <button
                  type="button"
                  onClick={() => handleMenuNavigate('/reservas')}
                  className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-subtle hover:text-text-primary"
                >
                  <Calendar className="h-4 w-4 shrink-0 text-primary" weight="Bold" />
                  {t('nav.myReservations')}
                </button>
              </div>

              <div className="border-t border-border-subtle p-1">
                <button
                  type="button"
                  onClick={handleMenuLogout}
                  className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-subtle hover:text-error"
                >
                  <Logout className="h-4 w-4 shrink-0" weight="Linear" />
                  {t('nav.signOut')}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <Link
            to="/entrar"
            className="inline-flex h-9 items-center rounded-lg bg-ink px-3.5 text-sm font-semibold text-ink-fg transition-colors hover:bg-ink-hover"
          >
            {t('nav.signIn')}
          </Link>
        )}
      </div>
    </header>
  )
}
